import { useEffect, useMemo, useState } from "react";
import { Empty, Modal, Radio, Skeleton } from "antd";
import { useMessage } from "../../../uteis/mensagens/useMessage";
import { ContainerAssinatura, TextAssinatura, TitleAssinatura } from "../styles";
import { CotaUsuarioAtiva } from "../interfaces/CotaUsuarioAtiva";
import { buscarCotasAtivasPessoaService } from "../services/buscarCotasAtivasPessoaService";
import { buscarAnuncioPropriedadeService } from "../services/BuscarAnuncioPropriedadeService";
import { anunciopPropriedadeService } from "../services/anuncioPropriedadeService";
import { vincularCotaAssinaturaService } from "../services/vincularCotaAssinaturaService";

interface ModalVincularCotaAssinaturaProps {
  isModalOpen: boolean;
  handleFechar: () => void;
  assinaturaId: number | null;
  assinaturaStatus: string | null;
  pessoaId: number | null;
  cotaUsuarioIdAtual: number | null;
  fetchAssinaturas: (showLoading?: boolean) => Promise<void>;
}

export default function ModalVincularCotaAssinatura({
  isModalOpen,
  handleFechar,
  assinaturaId,
  assinaturaStatus,
  pessoaId,
  cotaUsuarioIdAtual,
  fetchAssinaturas,
}: ModalVincularCotaAssinaturaProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cotas, setCotas] = useState<CotaUsuarioAtiva[]>([]);
  const [cotaSelecionadaId, setCotaSelecionadaId] = useState<number | null>(
    null
  );

  const { showError, showSuccess, contextHolder } = useMessage();

  useEffect(() => {
    const buscarCotas = async () => {
      if (!isModalOpen || !pessoaId) {
        return;
      }

      setIsLoading(true);
      setCotaSelecionadaId(null);

      try {
        const response = await buscarCotasAtivasPessoaService(pessoaId);
        const cotasSemAnuncio = await Promise.all(
          response.map(async (cota) => {
            const possuiAnuncioAtivo = await buscarAnuncioPropriedadeService(
              cota.id
            );

            return possuiAnuncioAtivo ? null : cota;
          })
        );

        const cotasDisponiveis = cotasSemAnuncio.filter(
          (cota): cota is CotaUsuarioAtiva => cota !== null
        );

        setCotas(cotasDisponiveis);
        setCotaSelecionadaId(
          cotasDisponiveis.some((cota) => cota.id === cotaUsuarioIdAtual)
            ? cotaUsuarioIdAtual
            : null
        );
      } catch (error: any) {
        const backendMessage = error?.response?.data?.message;

        if (Array.isArray(backendMessage) && backendMessage.length > 0) {
          showError(backendMessage[0]);
        } else if (typeof backendMessage === "string") {
          showError(backendMessage);
        } else {
          showError("Nao foi possivel carregar as cotas ativas do usuario.");
        }

        setCotas([]);
      } finally {
        setIsLoading(false);
      }
    };

    buscarCotas();
  }, [isModalOpen, pessoaId, cotaUsuarioIdAtual, showError]);

  const descricaoSelecionada = useMemo(() => {
    const cotaSelecionada = cotas.find((cota) => cota.id === cotaSelecionadaId);

    if (!cotaSelecionada) {
      return null;
    }

    return `Cota ${cotaSelecionada.numeroCota} - ${cotaSelecionada.nomeEmpreendimento}`;
  }, [cotaSelecionadaId, cotas]);

  const handleSelecionarCota = (cotaId: number) => {
    setCotaSelecionadaId(cotaId);
  };

  const handleVincular = async () => {
    if (!assinaturaId) {
      showError("Nao foi possivel identificar a assinatura selecionada.");
      return;
    }

    if (!cotaSelecionadaId) {
      showError("Selecione uma cota para vincular a assinatura.");
      return;
    }

    if (
      cotaUsuarioIdAtual &&
      cotaSelecionadaId !== cotaUsuarioIdAtual
    ) {
      const possuiAnuncioAtivo = await buscarAnuncioPropriedadeService(
        cotaUsuarioIdAtual
      );

      if (possuiAnuncioAtivo) {
        showError(
          "Nao e possivel retirar a assinatura desta cota, pois ela ja possui anuncio ativo."
        );
        return;
      }
    }

    setIsSaving(true);

    try {
      await vincularCotaAssinaturaService(assinaturaId, cotaSelecionadaId);

      if (assinaturaStatus === "ATIVA") {
        const anuncioCriado = await anunciopPropriedadeService(
          cotaSelecionadaId
        );

        if (!anuncioCriado) {
          showError(
            "A cota foi vinculada, mas nao foi possivel criar o anuncio da propriedade."
          );
          await fetchAssinaturas(false);
          return;
        }
      }

      await fetchAssinaturas(false);
      showSuccess("Cota vinculada a assinatura com sucesso!");
      setTimeout(() => {
        handleFechar();
      }, 1200);
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message;

      if (Array.isArray(backendMessage) && backendMessage.length > 0) {
        showError(backendMessage[0]);
      } else if (typeof backendMessage === "string") {
        showError(backendMessage);
      } else {
        showError("Nao foi possivel vincular a cota a assinatura.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        width={700}
        open={isModalOpen}
        onCancel={handleFechar}
        cancelText="Cancelar"
        okText="Vincular"
        onOk={handleVincular}
        confirmLoading={isSaving}
        okButtonProps={{
          disabled: isLoading || cotas.length === 0,
          style: {
            backgroundColor: "#003644",
            borderColor: "#003644",
            color: "white",
          },
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: "#f44336",
            borderColor: "#f44336",
            color: "white",
          },
        }}
      >
        <ContainerAssinatura>
          <TitleAssinatura>Vincular cota</TitleAssinatura>

          {isLoading ? (
            <Skeleton active />
          ) : cotas.length === 0 ? (
            <div style={{ marginTop: 24 }}>
              <Empty description="Este usuario nao possui cotas disponiveis para vincular." />
            </div>
          ) : (
            <>
              <TextAssinatura style={{ marginBottom: 16 }}>
                Selecione a cota que sera vinculada a assinatura.
              </TextAssinatura>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                {cotas.map((cota) => {
                  const selecionada = cotaSelecionadaId === cota.id;

                  return (
                    <div
                      key={cota.id}
                      onClick={() => handleSelecionarCota(cota.id)}
                      style={{
                        border: selecionada
                          ? "1px solid #1677ff"
                          : "1px solid #d9d9d9",
                        backgroundColor: selecionada ? "#f0f7ff" : "#ffffff",
                        borderRadius: 8,
                        padding: 12,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        cursor: "pointer",
                      }}
                    >
                      <Radio checked={selecionada} />
                      <div style={{ color: "#003644", lineHeight: 1.5 }}>
                        <div style={{ fontWeight: 700 }}>
                          Empreendimento: {cota.nomeEmpreendimento}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          Numero da cota: {cota.numeroCota}
                        </div>
                        <div style={{ marginTop: 4, color: "#5f6368" }}>
                          {cota.bloco
                            ? `Bloco ${cota.bloco} - Apto ${cota.numeroApto}`
                            : `Apto ${cota.numeroApto}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {descricaoSelecionada ? (
                <TextAssinatura style={{ marginTop: 16, marginBottom: 0 }}>
                  Selecionada: {descricaoSelecionada}
                </TextAssinatura>
              ) : null}
            </>
          )}
        </ContainerAssinatura>
      </Modal>
    </>
  );
}
